export type ResponseFieldDefinition = {
  path: string;
  label: string;
  format: "text" | "number" | "boolean" | "datetime" | "link" | "json";
};

export type ResponseDisplayDefinition = {
  mode: "JSON" | "KEY_VALUE" | "LIST" | "TABLE" | "TEXT";
  root?: string;
  fields?: ResponseFieldDefinition[];
  summaryRoot?: string;
  summaryFields?: ResponseFieldDefinition[];
};

function escapeHTML(value: string): string {
  return value.replace(/[&<>"']/g, character => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]!);
}

export function parseResponseDefinition(raw: string): ResponseDisplayDefinition {
  try {
    const parsed = JSON.parse(raw || "{}") as ResponseDisplayDefinition;
    return {
      mode: parsed.mode || "JSON",
      root: parsed.root || "",
      fields: parsed.fields || [],
      summaryRoot: parsed.summaryRoot || "",
      summaryFields: parsed.summaryFields || [],
    };
  } catch {
    return { mode: "JSON", fields: [] };
  }
}

export function valueAtPath(source: unknown, path: string): unknown {
  if (!path) return source;
  return path.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object") return (current as Record<string, unknown>)[key];
    return undefined;
  }, source);
}

function formatDisplayValue(value: unknown, format: string): string {
  if (value === null || value === undefined) return "—";
  if (format === "boolean") return value ? "是" : "否";
  if (format === "datetime") {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("zh-CN");
  }
  if (format === "json" || typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function displayValueHTML(value: unknown, format: string): string {
  const rendered = formatDisplayValue(value, format);
  if (format === "link" && /^https?:\/\//i.test(rendered)) {
    return `<a href="${escapeHTML(rendered)}" target="_blank" rel="noreferrer">${escapeHTML(rendered)}</a>`;
  }
  return escapeHTML(rendered);
}

function keyValueRowsHTML(source: unknown, fields: ResponseFieldDefinition[]): string {
  return `<dl class="rendered-key-values">${fields.map(field => `
    <div>
      <dt>${escapeHTML(field.label)}</dt>
      <dd class="${field.format === "json" ? "json-value" : ""}">${displayValueHTML(valueAtPath(source, field.path), field.format)}</dd>
    </div>`).join("")}
  </dl>`;
}

export function configuredResponseBodyHTML(
  definition: ResponseDisplayDefinition,
  result: unknown,
): string {
  const root = valueAtPath(result, definition.root || "");
  const fields = definition.fields || [];
  if (definition.mode === "TEXT") {
    return `<p class="rendered-text">${displayValueHTML(root, "text")}</p>`;
  }
  if (definition.mode === "KEY_VALUE") {
    return keyValueRowsHTML(root, fields);
  }
  if (definition.mode === "LIST") {
    const rows = Array.isArray(root) ? root : [];
    const summaryFields = definition.summaryFields || [];
    const summary = summaryFields.length === 0
      ? ""
      : `<section class="rendered-result-summary">
          <header><span>SUMMARY</span><strong>总体结果</strong></header>
          ${keyValueRowsHTML(valueAtPath(result, definition.summaryRoot || ""), summaryFields)}
        </section>`;
    if (rows.length === 0) {
      return `${summary}<p class="rendered-empty">未提取到数组结果，请检查后台配置的数据根路径。</p>`;
    }
    return `${summary}<div class="rendered-result-list">${rows.map((row, index) => `
        <section class="rendered-result-group">
          <header><span>RESULT</span><strong>记录 ${String(index + 1).padStart(2, "0")}</strong></header>
          ${keyValueRowsHTML(row, fields)}
        </section>`).join("")}
      </div>`;
  }
  if (definition.mode === "TABLE") {
    const rows = Array.isArray(root) ? root : [];
    return `<div class="rendered-table-wrap"><table><thead><tr>${fields.map(field => `<th>${escapeHTML(field.label)}</th>`).join("")}</tr></thead><tbody>${rows.map(row => `<tr>${fields.map(field => `<td>${displayValueHTML(valueAtPath(row, field.path), field.format)}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`;
  }
  return `<pre class="rendered-json">${escapeHTML(JSON.stringify(result, null, 2))}</pre>`;
}
